// client/src/components/communication/VideoSessionInvitation.jsx
import React, { useState, useEffect } from 'react';
import '../../../styles/components/communication/VideoSessionInvitation.css';

const VideoSessionInvitation = ({
  sessionId,
  sessionTime,
  sessionDuration = 60,
  status = 'scheduled',
  canJoin = false,
  onJoinSession,
  userRole = 'patient', // 'doctor' or 'patient'
  appointmentId = null
}) => {
  const [timeUntilSession, setTimeUntilSession] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(status);
  const [isJoining, setIsJoining] = useState(false);

  // Update countdown timer
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const sessionDate = new Date(sessionTime);
      const diffMinutes = Math.floor((sessionDate - now) / (1000 * 60));
      
      setTimeUntilSession(diffMinutes);
    };

    // Update immediately
    updateTimer();

    // Update every minute
    const interval = setInterval(updateTimer, 60000);

    return () => clearInterval(interval);
  }, [sessionTime]);

  // Handle join session
  const handleJoinSession = async () => {
    if (!canJoin || isJoining) return;
    
    try {
      setIsJoining(true);
      await onJoinSession(sessionId);
    } catch (error) {
      console.error('Error joining session:', error);
    } finally {
      setIsJoining(false);
    }
  };

  // Format session time for display
  const formatSessionTime = () => {
    const date = new Date(sessionTime);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeString = date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${timeString}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${timeString}`;
    } else {
      return `${date.toLocaleDateString()} at ${timeString}`;
    }
  };

  // Get status display info
  const getStatusInfo = () => {
    switch (sessionStatus) {
      case 'scheduled':
        if (timeUntilSession === null) return { text: 'Scheduled', color: '#6B7280', icon: '📅' };
        if (timeUntilSession <= 0 && timeUntilSession >= -15) {
          return { text: 'Ready to join', color: '#10B981', icon: '🟢' };
        }
        if (timeUntilSession > 0) {
          return { 
            text: timeUntilSession === 1 ? '1 minute' : `${timeUntilSession} minutes`,
            color: '#F59E0B', 
            icon: '⏰' 
          };
        }
        return { text: 'Session missed', color: '#EF4444', icon: '❌' };
      
      case 'starting':
        return { text: 'Starting...', color: '#8B5CF6', icon: '🚀' };
      
      case 'active':
        return { text: 'Session active', color: '#EF4444', icon: '🔴' };
      
      case 'ended':
        return { text: 'Session completed', color: '#10B981', icon: '✅' };
      
      case 'cancelled':
        return { text: 'Session cancelled', color: '#6B7280', icon: '❌' };
      
      default:
        return { text: 'Unknown status', color: '#6B7280', icon: '❓' };
    }
  };

  const statusInfo = getStatusInfo();

  // Determine if join button should be shown
  const showJoinButton = () => {
    if (sessionStatus === 'ended' || sessionStatus === 'cancelled') return false;
    if (sessionStatus === 'active') return true; // Allow rejoining
    return canJoin && timeUntilSession !== null && timeUntilSession <= 5 && timeUntilSession >= -15;
  };

  // Get join button text
  const getJoinButtonText = () => {
    if (isJoining) return 'Joining...';
    if (sessionStatus === 'active') return 'Rejoin Session';
    return 'Join Session';
  };

  return (
    <div className="video-session-invitation">
      {/* Session Header */}
      <div className="session-header">
        <div className="session-icon">
          <span className="video-icon">📹</span>
        </div>
        <div className="session-title">
          <h4>Video Therapy Session</h4>
          <p className="session-subtitle">
            {userRole === 'doctor' ? 'Session with patient' : 'Session with your therapist'}
          </p>
        </div>
        <div className="session-status">
          <span 
            className="status-indicator"
            style={{ color: statusInfo.color }}
          >
            {statusInfo.icon} {statusInfo.text}
          </span>
        </div>
      </div>

      {/* Session Details */}
      <div className="session-details">
        <div className="detail-row">
          <span className="detail-icon">🕒</span>
          <span className="detail-text">{formatSessionTime()}</span>
        </div>
        <div className="detail-row">
          <span className="detail-icon">⏱️</span>
          <span className="detail-text">{sessionDuration} minutes</span>
        </div>
        {sessionId && (
          <div className="detail-row">
            <span className="detail-icon">🆔</span>
            <span className="detail-text session-id">
              Session ID: {sessionId.split('_')[1]?.substring(0, 8) || 'Unknown'}
            </span>
          </div>
        )}
      </div>

      {/* Session Actions */}
      <div className="session-actions">
        {showJoinButton() && (
          <button
            className={`join-session-btn ${sessionStatus === 'active' ? 'rejoin' : 'join'}`}
            onClick={handleJoinSession}
            disabled={isJoining}
          >
            {isJoining && <span className="loading-spinner"></span>}
            <span className="btn-icon">📹</span>
            <span className="btn-text">{getJoinButtonText()}</span>
          </button>
        )}

        {sessionStatus === 'scheduled' && timeUntilSession > 5 && (
          <div className="session-waiting">
            <span className="waiting-icon">⏳</span>
            <span className="waiting-text">
              You can join this session {timeUntilSession - 5} minutes from now
            </span>
          </div>
        )}

        {sessionStatus === 'ended' && (
          <div className="session-completed">
            <span className="completed-icon">✅</span>
            <span className="completed-text">
              This session has been completed
            </span>
          </div>
        )}

        {sessionStatus === 'cancelled' && (
          <div className="session-cancelled">
            <span className="cancelled-icon">❌</span>
            <span className="cancelled-text">
              This session has been cancelled
            </span>
          </div>
        )}
      </div>

      {/* Additional Info */}
      {sessionStatus === 'scheduled' && (
        <div className="session-info">
          <div className="info-item">
            <span className="info-icon">ℹ️</span>
            <span className="info-text">
              You'll be able to join this session 5 minutes before the scheduled time.
            </span>
          </div>
          {userRole === 'patient' && (
            <div className="info-item">
              <span className="info-icon">🔒</span>
              <span className="info-text">
                This is a secure, encrypted video session with your healthcare provider.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Session Instructions for Active State */}
      {sessionStatus === 'active' && (
        <div className="session-active-info">
          <div className="active-indicator">
            <span className="pulse-dot"></span>
            <span className="active-text">Session is currently active</span>
          </div>
          <div className="active-instructions">
            Click "Rejoin Session" if you were disconnected or need to return to the video call.
          </div>
        </div>
      )}

      {/* Countdown for Soon-to-Start Sessions */}
      {sessionStatus === 'scheduled' && timeUntilSession !== null && timeUntilSession <= 15 && timeUntilSession > 0 && (
        <div className="session-countdown">
          <div className="countdown-header">
            <span className="countdown-icon">⏰</span>
            <span className="countdown-text">Session starts in:</span>
          </div>
          <div className="countdown-timer">
            <span className="countdown-minutes">{timeUntilSession}</span>
            <span className="countdown-label">minute{timeUntilSession !== 1 ? 's' : ''}</span>
          </div>
          {timeUntilSession <= 5 && (
            <div className="countdown-ready">
              <span className="ready-icon">🟢</span>
              <span className="ready-text">You can now join the session!</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoSessionInvitation;